import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
// import { useTranslation } from "react-i18next" TODO unused 제거
import { CircularProgress } from "@mui/material"

import Tooltip from "@/components/tooltip/Tooltip"
import { createDrawerStore } from "@/stores/global"
import {
  ChartMultiple,
  ChartMultipleItem,
  SessionDetailsData,
  ToolTipContents,
} from "@/app/report/common/constructor"
import {
  REPORT_SECTION_PADDING,
  REPORT_PADDING,
  DRAWER_WIDTH,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import { createChart, ChartStyle } from "@/app/report/common/chart/multiple"
import { colors } from "@/components/colors"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"

import {
  DEFAULT_CHART_WIDTH,
  DEFAULT_CHART_HEIGHT,
  CHART_RATIO,
  MIN_CHART_HEIGHT,
} from "@/app/report/common/export/constants"

function IntervalChart(
  {
    loading,
    chartData,
    scaleRatio,
    splitted,
    maxItemCount,
    decimalPoint = 1,
    chartAnnotation,
    highestValues,
  }: {
    loading: boolean
    chartData?: SessionDetailsData
    scaleRatio: number
    splitted: boolean
    /** 한 섹션이 여러 조각으로 나뉠 때, 모든 조각이 같은 항목 폭을 갖도록 하는 기준 개수 */
    maxItemCount?: number
    decimalPoint: number
    chartAnnotation: string
    highestValues?: number[]
  },
  ref: React.ForwardedRef<{
    drawChart: () => void
  }>,
) {
  // const { t } = useTranslation() TODO unused 제거

  // 차트 관련
  const elementLegendAndChart = useRef<HTMLDivElement>(null)
  const elementLegends = useRef<HTMLDivElement[]>(null)
  const elementChartContainer = useRef<HTMLDivElement>(null)
  const chartYAxisLeft = useRef<HTMLDivElement>(null)
  const chartElements = useRef<HTMLDivElement>(null)
  const chartYAxisRight = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState<number>(0)
  const [chartHeight, setChartHeight] = useState<number>(0)
  // tooltip 관련
  const [tooltipTitles] = useState<string[]>([])
  const [tooltipValues] = useState<Array<ToolTipContents>>([])
  const [tooltipVisible] = useState<boolean>(false)
  const svgYAxisLeft = useRef<SVGSVGElement>(null)
  const svgYAxisRight = useRef<SVGSVGElement>(null)
  // const elementHighlighted = useRef<HTMLDivElement>(null) TODO unused 제거

  // 리사이징 관련
  const { isOpen: isDrawerOpen, isMobile } = createDrawerStore()
  // handleResize 함수가 컴포넌트 렌더링 전에 실행되는 것을 방지하기 위해 useRef 사용
  const isMounted = useRef(false)

  useEffect(() => {
    elementLegends.current = []
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    window.addEventListener("resize", handleResize) // 리사이즈

    // 모바일에서는 drawer 상태 변경 시 resize 하지 않음 (overlay이므로)
    const isMobileScreen = window.innerWidth < 1200
    if (!isMobileScreen) {
      handleResize()
    }

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isDrawerOpen])

  useEffect(() => {
    if (!loading && chartData) {
      drawChart()
    }
  }, [chartData, chartWidth, chartHeight, loading])

  const translateKey = useTranslateMetricKey()

  const handleResize = () => {
    // PDF preview container 기준으로 계산하기
    const pdfContainer = document.querySelector(".ExportPdfPreviewCont")
    const containerWidth =
      pdfContainer?.clientWidth || document.querySelector(".Container")?.clientWidth
    if (containerWidth) {
      // 모바일에서는 drawer가 overlay이므로 width 조정하지 않음
      // store 타이밍 이슈로 window.innerWidth로 직접 체크
      const isMobileScreen = window.innerWidth < 1200
      const drawerOffset = isDrawerOpen && !isMobileScreen ? DRAWER_WIDTH : 0
      const currentChartWidth = pdfContainer
        ? containerWidth - 40 // PDF preview padding
        : containerWidth - drawerOffset - REPORT_PADDING - REPORT_SECTION_PADDING - 1

      if (isMounted.current) {
        setChartWidth(currentChartWidth)
        // 세로 크롬이 고정 px 이라 폭이 좁아지면 플롯 영역이 먼저 사라진다 — 하한을 둔다.
        setChartHeight(Math.max(currentChartWidth * CHART_RATIO, MIN_CHART_HEIGHT))
      }
    }
  }

  // 초기 마운트 시 resize 실행
  useEffect(() => {
    handleResize()
  }, [])

  const drawChart = useCallback(() => {
    if (!chartData) {
      return
    }

    if (
      !elementChartContainer.current ||
      !chartYAxisLeft.current ||
      !chartElements.current ||
      !chartYAxisRight.current
    ) {
      return
    }

    const charts = chartData.dataInterval.map(
      (chart) => new ChartMultipleItem(chart.name, chart.nickname, chart.data, highestValues),
    )
    const chartMultiple = new ChartMultiple(chartData.axisY, charts)

    const chartStyle = new ChartStyle({
      // chartWidth 는 이미 실측 컨테이너 폭 — scaleRatio 를 다시 곱하면 이중 스케일로
      // 컨테이너보다 넓게 그려져 우측이 잘린다(WEB-2259). fallback 상수에만 배율 적용.
      width: chartWidth || DEFAULT_CHART_WIDTH * (scaleRatio || 1),
      height: chartHeight || DEFAULT_CHART_HEIGHT * (scaleRatio || 1),
      magnification: 1,
    })
    chartStyle.fullWidth = false
    chartStyle.alignLeft = false
    // 자투리 조각(예: 14개씩 자르고 3개 남은 마지막 조각)도 앞 조각과 같은 막대 폭을 쓰게 한다.
    chartStyle.referenceXAxisItemCount = maxItemCount ?? 0
    chartStyle.flagShowTickStroke = false
    chartStyle.arrayWidthOfBar = [17, 17, 13]
    // 축 영역을 좁혀 차트 폭을 되찾는다. 출력용은 폰트가 작아(8px) 화면 기준 여백이 과하다.
    // 실측(축 3개): 우측 축 176px 중 눈금 글자 4개(각 5px)와 세로 라벨 2개(두께 ~12px)를 빼면
    // 100px 이상이 빈 공간이었다.
    chartStyle.space.yAxisLabelWidth = 16
    chartStyle.space.betweenRightYAxis = 6
    chartStyle.minYAxisTickValueWidth = 14
    chartStyle.valuePositionType = chartAnnotation as VALUE_POSITION_TYPE
    chartStyle.isPreview = true
    chartStyle.fontSize = 8
    chartStyle.fontSizeValue = 8
    chartStyle.fontSizeNoData = 8

    if (chartMultiple.charts.length === 1) {
      chartStyle.space.xAxisChartNameWidth = 0
    }

    const result = createChart({
      chartMultiple,
      style: chartStyle,
      translateKey,
      mouseover: () => {},
      mousemove: () => {},
      mouseout: () => {},
      decimalPointForExportFile: decimalPoint,
    })

    if (
      result &&
      elementChartContainer.current &&
      chartYAxisLeft.current &&
      chartElements.current &&
      chartYAxisRight.current
    ) {
      chartElements.current.innerHTML = ""
      chartYAxisLeft.current.innerHTML = ""
      chartYAxisRight.current.innerHTML = ""

      if (result.svgYAxisLeft) {
        const nodeDiv = document.createElement("div")
        nodeDiv.style.width = `${result.svgYAxisLeft.viewBox.baseVal.width}px`
        nodeDiv.style.height = `${result.svgYAxisLeft.viewBox.baseVal.height}px`
        nodeDiv.appendChild(result.svgYAxisLeft)
        chartYAxisLeft.current.appendChild(nodeDiv)
        svgYAxisLeft.current = result.svgYAxisLeft
      }
      if (result.svgYAxisRight) {
        const nodeDiv = document.createElement("div")
        nodeDiv.style.width = `${result.svgYAxisRight.viewBox.baseVal.width}px`
        nodeDiv.style.height = `${result.svgYAxisRight.viewBox.baseVal.height}px`
        nodeDiv.appendChild(result.svgYAxisRight)
        chartYAxisRight.current.appendChild(nodeDiv)
        svgYAxisRight.current = result.svgYAxisRight
      }

      const svgCharts = []
      result.svgCharts.forEach((svgChart) => {
        if (svgChart) {
          const nodeDiv = document.createElement("div")
          nodeDiv.style.width = `${svgChart.viewBox.baseVal.width}px`
          nodeDiv.style.height = `${svgChart.viewBox.baseVal.height}px`
          nodeDiv.appendChild(svgChart)
          chartElements.current?.appendChild(nodeDiv)
          svgCharts.push(svgChart)
        }
      })
    }
  }, [chartData, chartWidth, chartHeight])

  useImperativeHandle(ref, () => ({
    drawChart,
    exportFile: async () => {},
  }))

  // TODO 로딩 컴포넌트
  if (loading) {
    return (
      <div className="MuiLoadingSpinnerCont">
        <CircularProgress size={48} />
      </div>
    )
  }

  return (
    <div className="IntervalChartCont">
      <Tooltip
        type="chart"
        label={tooltipTitles[0]}
        title={
          <div className="bar-chart-tooltip">
            {tooltipValues.map((value, index) => {
              if (!value.value || !value.title) {
                return null
              }

              return (
                <div key={index} className="bar-chart-tooltip-item">
                  <span
                    style={{
                      backgroundColor: value.color || colors.neutral.black,
                      color: value.color || colors.neutral.black,
                    }}
                  />
                  <p>{value.title}:</p>
                  <p>{value.value}</p>
                </div>
              )
            })}
          </div>
        }
        size="medium"
        open={tooltipVisible}>
        <div className="chart">
          <div ref={elementLegendAndChart} className="element-legend-and-chart">
            <div className="legend">
              {chartData?.axisY.map((axisY, index) => {
                const newAxisKey = translateKey(axisY.key)

                return (
                  <div key={index}>
                    <div
                      ref={(el) => {
                        if (el && elementLegends.current) {
                          elementLegends.current[index] = el
                        }
                      }}
                      className="element-legend">
                      <span
                        style={{
                          color: axisY.color,
                        }}>
                        ●
                      </span>
                      <span className="legend-text">{newAxisKey}</span>
                    </div>
                  </div>
                )
              })}
            </div>
            <div ref={elementChartContainer} className="element-chart-container preview">
              <table>
                <tbody>
                  <tr>
                    <td className="chart-y-axis-left">
                      <div ref={chartYAxisLeft}></div>
                    </td>
                    <td>
                      <div ref={chartElements} className="chart-elements"></div>
                    </td>
                    <td className="chart-y-axis-right">
                      <div ref={chartYAxisRight}></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Tooltip>
    </div>
  )
}

export default forwardRef(IntervalChart)
